import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, X, MessageSquare } from 'lucide-react';

export default function FloatingBadge() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.8 }}
          transition={{ type: "spring", damping: 20, stiffness: 300 }}
          className="fixed bottom-6 right-6 z-50"
          data-testid="floating-review-badge"
        >
          <AnimatePresence mode="wait">
            {!isExpanded ? (
              <motion.button
                key="collapsed"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsExpanded(true)}
                className="float-animation glass rounded-2xl p-4 shadow-2xl border border-[#38BDF8]/30 cursor-pointer group"
              >
                <div className="flex items-center gap-3">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg" 
                    alt="Google" 
                    className="w-8 h-8"
                  />
                  <div className="text-left">
                    <div className="flex items-center gap-1">
                      <span className="text-xl font-bold text-white">5.0</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-[#94A3B8]">114 Google Reviews</div>
                  </div>
                </div>
              </motion.button>
            ) : (
              <motion.div
                key="expanded"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="glass rounded-2xl p-6 shadow-2xl border border-[#38BDF8]/30 w-80"
              >
                <button
                  onClick={() => setIsExpanded(false)}
                  className="absolute top-3 right-3 p-1 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-[#94A3B8]" />
                </button>

                <div className="flex items-center gap-4 mb-4">
                  <img 
                    src="https://upload.wikimedia.org/wikipedia/commons/5/53/Google_%22G%22_Logo.svg" 
                    alt="Google" 
                    className="w-12 h-12"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-3xl font-bold text-white">5.0</span>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-5 h-5 fill-[#FBBF24] text-[#FBBF24]" />
                        ))}
                      </div>
                    </div>
                    <div className="text-sm text-[#94A3B8]">Based on 114 reviews</div>
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[#94A3B8] w-16">5 stars</span>
                    <div className="flex-1 h-2 bg-[#0B111E] rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: '96%' }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="h-full bg-[#FBBF24] rounded-full" 
                      />
                    </div>
                    <span className="text-sm text-white w-8">111</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[#94A3B8] w-16">4 stars</span>
                    <div className="flex-1 h-2 bg-[#0B111E] rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: '4%' }}
                        transition={{ duration: 0.8, delay: 0.3 }}
                        className="h-full bg-[#FBBF24] rounded-full" 
                      />
                    </div>
                    <span className="text-sm text-white w-8">3</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <motion.a
                    href="#reviews"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsExpanded(false)}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#38BDF8]/20 text-[#38BDF8] font-medium text-sm hover:bg-[#38BDF8]/30 transition-colors"
                  >
                    <MessageSquare className="w-4 h-4" />
                    Read All
                  </motion.a>
                  <motion.a
                    href="#quote"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setIsExpanded(false)}
                    className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl cta-button text-white font-medium text-sm"
                  >
                    Get Quote
                  </motion.a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
